"use client";

import { c as _c } from "react/compiler-runtime";
import type { QueryStatus } from "@tanstack/react-query";
import type { ReactNode } from "react";
import type { ApiWave } from "@/generated/models/ApiWave";
import { getScaledImageUri, ImageScale } from "@/helpers/image.helpers";
import { getWaveRoute } from "@/helpers/navigation.helpers";
import { ChatBubbleLeftRightIcon, PlusIcon } from "@heroicons/react/24/outline";
import Link from "next/link";
import Image from "next/image";
import WavesIcon from "@/components/common/icons/WavesIcon";
interface UserPageBrainSidebarMobileStripProps {
  readonly createdWaves: ApiWave[];
  readonly createdStatus: QueryStatus;
  readonly mostActiveWaves: ApiWave[];
  readonly mostActiveStatus: QueryStatus;
  readonly onOpenCreatedWaves: () => void;
}
function UserPageBrainSidebarMobileWavePill(t0) {
  const $ = _c(15);
  const {
    wave
  } = t0;
  const isDirectMessage = wave.chat.scope.group?.is_direct_message ?? false;
  let t1;
  if ($[0] !== isDirectMessage || $[1] !== wave.id) {
    t1 = getWaveRoute({
      waveId: wave.id,
      isDirectMessage,
      isApp: false
    });
    $[0] = isDirectMessage;
    $[1] = wave.id;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const href = t1;
  let t2;
  if ($[3] !== wave.picture) {
    t2 = wave.picture ? getScaledImageUri(wave.picture, ImageScale.W_200_H_200) : null;
    $[3] = wave.picture;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const imageSrc = t2;
  const FallbackIcon = isDirectMessage ? ChatBubbleLeftRightIcon : WavesIcon;
  let t3;
  if ($[5] !== FallbackIcon || $[6] !== imageSrc || $[7] !== wave.name) {
    t3 = <div className="tw-relative tw-flex tw-h-7 tw-w-7 tw-shrink-0 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-full tw-border tw-border-solid tw-border-white/10 tw-bg-iron-900">{imageSrc ? <Image src={imageSrc} alt={wave.name ? `Wave ${wave.name}` : "Wave picture"} fill={true} sizes="28px" className="tw-object-cover tw-transition-transform tw-duration-300 desktop-hover:group-hover:tw-scale-105" /> : <FallbackIcon className="tw-h-3.5 tw-w-3.5 tw-flex-shrink-0 tw-text-iron-300" />}</div>;
    $[5] = FallbackIcon;
    $[6] = imageSrc;
    $[7] = wave.name;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== wave.name) {
    t4 = <span className="tw-min-w-0 tw-truncate tw-text-xs tw-font-medium tw-text-iron-300 tw-transition-colors desktop-hover:group-hover:tw-text-iron-100">{wave.name}</span>;
    $[9] = wave.name;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== href || $[12] !== t3 || $[13] !== t4) {
    t5 = <Link href={href} prefetch={false} className="tw-group tw-inline-flex tw-h-9 tw-max-w-[14rem] tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-solid tw-border-white/[0.08] tw-bg-iron-950 tw-p-1 tw-pr-3 tw-no-underline tw-shadow-sm tw-transition-all tw-duration-200 desktop-hover:hover:tw-border-white/[0.15] desktop-hover:hover:tw-bg-white/[0.05]">{t3}{t4}</Link>;
    $[11] = href;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
}
function MobileWavePillSkeleton(t0) {
  const $ = _c(4);
  const {
    keyId
  } = t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="tw-h-7 tw-w-7 tw-shrink-0 tw-animate-pulse tw-rounded-full tw-bg-white/[0.07]" />;
    t2 = <div className="tw-h-3 tw-w-20 tw-animate-pulse tw-rounded tw-bg-white/[0.07]" />;
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] !== keyId) {
    t3 = <div key={keyId} className="tw-inline-flex tw-h-9 tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-solid tw-border-white/[0.06] tw-bg-iron-950/70 tw-p-1 tw-pr-3">{t1}{t2}</div>;
    $[2] = keyId;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}
export default function UserPageBrainSidebarMobileStrip(t0) {
  const $ = _c(23);
  const {
    createdWaves,
    createdStatus,
    mostActiveWaves,
    mostActiveStatus,
    onOpenCreatedWaves
  } = t0;
  const shouldShowCreatedLoading = createdStatus === "pending" && createdWaves.length === 0;
  const shouldShowCreatedWaves = createdWaves.length > 0;
  const shouldShowMostActiveLoading = mostActiveStatus === "pending" && mostActiveWaves.length === 0;
  const shouldShowMostActiveWaves = mostActiveWaves.length > 0;
  const shouldShowCreatedSection = shouldShowCreatedLoading || shouldShowCreatedWaves;
  const shouldShowMostActiveSection = shouldShowMostActiveLoading || shouldShowMostActiveWaves;
  if (!shouldShowCreatedSection && !shouldShowMostActiveSection) {
    return null;
  }
  const firstCreatedWave = createdWaves[0];
  const remainingCreatedCount = Math.max(createdWaves.length - 1, 0);
  let createdSectionContent = null;
  if (shouldShowCreatedLoading) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <><MobileWavePillSkeleton keyId="created-0" /><MobileWavePillSkeleton keyId="created-1" /></>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    createdSectionContent = t1;
  } else {
    if (firstCreatedWave) {
      let t1;
      if ($[1] !== firstCreatedWave) {
        t1 = <UserPageBrainSidebarMobileWavePill wave={firstCreatedWave} />;
        $[1] = firstCreatedWave;
        $[2] = t1;
      } else {
        t1 = $[2];
      }
      let t2;
      if ($[3] !== onOpenCreatedWaves || $[4] !== remainingCreatedCount) {
        t2 = remainingCreatedCount > 0 && <button type="button" onClick={onOpenCreatedWaves} className="tw-inline-flex tw-h-9 tw-items-center tw-gap-1.5 tw-rounded-lg tw-border tw-border-solid tw-border-white/[0.08] tw-bg-iron-950 tw-px-3 tw-text-xs tw-font-semibold tw-text-iron-400 tw-shadow-sm tw-transition-all tw-duration-200 desktop-hover:hover:tw-border-white/[0.15] desktop-hover:hover:tw-bg-white/[0.05] desktop-hover:hover:tw-text-iron-100" aria-label="View all created waves"><PlusIcon className="tw-h-3 tw-w-3 tw-flex-shrink-0" /><span>{remainingCreatedCount}</span></button>;
        $[3] = onOpenCreatedWaves;
        $[4] = remainingCreatedCount;
        $[5] = t2;
      } else {
        t2 = $[5];
      }
      let t3;
      if ($[6] !== t1 || $[7] !== t2) {
        t3 = <>{t1}{t2}</>;
        $[6] = t1;
        $[7] = t2;
        $[8] = t3;
      } else {
        t3 = $[8];
      }
      createdSectionContent = t3;
    }
  }
  let t1;
  if ($[9] !== createdSectionContent || $[10] !== shouldShowCreatedSection) {
    t1 = shouldShowCreatedSection && <div className="tw-flex tw-shrink-0 tw-flex-col tw-gap-2"><span className="tw-px-1 tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-wider tw-text-iron-500">Created</span><div className="tw-flex tw-items-center tw-gap-2">{createdSectionContent}</div></div>;
    $[9] = createdSectionContent;
    $[10] = shouldShowCreatedSection;
    $[11] = t1;
  } else {
    t1 = $[11];
  }
  let t2;
  if ($[12] !== shouldShowCreatedSection || $[13] !== shouldShowMostActiveSection) {
    t2 = shouldShowCreatedSection && shouldShowMostActiveSection && <div className="tw-mb-1 tw-h-11 tw-w-px tw-shrink-0 tw-bg-white/5" />;
    $[12] = shouldShowCreatedSection;
    $[13] = shouldShowMostActiveSection;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  let t3;
  if ($[15] !== mostActiveWaves || $[16] !== shouldShowMostActiveLoading || $[17] !== shouldShowMostActiveSection) {
    t3 = shouldShowMostActiveSection && <div className="tw-flex tw-shrink-0 tw-flex-col tw-gap-2"><span className="tw-px-1 tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-wider tw-text-iron-500">Active In</span><div className="tw-flex tw-items-center tw-gap-2">{shouldShowMostActiveLoading ? [0, 1, 2].map(_temp) : mostActiveWaves.slice(0, 3).map(_temp2)}</div></div>;
    $[15] = mostActiveWaves;
    $[16] = shouldShowMostActiveLoading;
    $[17] = shouldShowMostActiveSection;
    $[18] = t3;
  } else {
    t3 = $[18];
  }
  let t4;
  if ($[19] !== t1 || $[20] !== t2 || $[21] !== t3) {
    t4 = <section aria-label="Brain waves strip" className="lg:tw-hidden" data-testid="brain-sidebar-mobile-strip"><div className="tw-relative tw-overflow-hidden"><div className="tw-flex tw-items-end tw-gap-4 tw-overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:tw-hidden">{t1}{t2}{t3}</div></div></section>;
    $[19] = t1;
    $[20] = t2;
    $[21] = t3;
    $[22] = t4;
  } else {
    t4 = $[22];
  }
  return t4;
}
function _temp2(wave) {
  return <UserPageBrainSidebarMobileWavePill key={wave.id} wave={wave} />;
}
function _temp(key) {
  return <MobileWavePillSkeleton key={`most-active-${key}`} keyId={`most-active-${key}`} />;
}